import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { careerColor } from 'services/careerTokens';
import { careerType } from 'services/careerType';

import { Block, BlockTitle, Meta } from './passportStyles';

interface ISkillPassportCriteria {
  requiredLevel: number;
  criteria: { level: number; points: string[]; example: string }[];
  /** Детальный запрос ещё не пришёл — не говорим «описания нет» раньше времени. */
  loading: boolean;
}

/**
 * «Co trzeba umieć na poziomie N» (ТЗ 0072, 3.3, пункт 3).
 *
 * Аудит: «Zdobądź tę umiejętność na poziomie 3» не объясняла, что конкретно
 * нужно уметь. Пункты пишет методист в справочнике ERP — 3–5 наблюдаемых
 * результатов на уровень.
 *
 * 🔴 Нет пунктов — честное «opis w przygotowaniu», а не придуманные
 * автоматически требования: выдуманный критерий хуже отсутствующего, по нему
 * человек решит, что уже умеет.
 */
export const SkillPassportCriteria = memo(
  ({ requiredLevel, criteria, loading }: ISkillPassportCriteria) => {
    const { t } = useTranslation();
    const row = criteria.find((item) => item.level === requiredLevel);
    const points = row?.points ?? [];
    const example = row?.example ?? '';

    return (
      <Block aria-labelledby="skm-passport-criteria">
        <BlockTitle id="skm-passport-criteria">
          {t('careerCenter.map.criteria.title', { n: requiredLevel })}
        </BlockTitle>
        {points.length ? (
          <List>
            {points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </List>
        ) : loading ? null : (
          <>
            <Meta>{t('careerCenter.map.criteria.empty')}</Meta>
            {/* 0073: самооценка по общей шкале остаётся доступной — и названа
                самооценкой, а не проверкой LearnIT. */}
            <Meta>{t('careerCenter.map.criteria.generalScale')}</Meta>
          </>
        )}
        {example ? (
          <Example>{t('careerCenter.map.criteria.example', { text: example })}</Example>
        ) : null}
      </Block>
    );
  },
);

const Example = styled.p`
  margin: 0;
  color: ${careerColor.text2};
  ${careerType.meta()};
`;

const List = styled.ul`
  margin: 0;
  padding-left: 20px;
  color: ${careerColor.text};
  ${careerType.body()};

  li + li {
    margin-top: 4px;
  }
`;
